import { Injectable, inject, signal } from '@angular/core';
import { createDocument, Editor } from '@tiptap/core';
import { EditorState } from '@tiptap/pm/state';
import { LinkMenuState, LinkMenuSubmission, SpellcheckSuggestionMenuState } from '../models/sh-editor-overlay.model';
import { ToolbarCommand } from '../models/sh-editor-prosemirror.model';
import { ActiveFormatsState, ShEditorStateServiceContext } from '../models/sh-editor-state.model';
import { clearSpellcheckDecorations, updateSpellcheckDecorations } from '../plugins/sh-editor-spellcheck.plugin';
import { ShEditorSpellcheckService } from './sh-editor-spellcheck.service';
import { normalizeEditorLinkHref } from '../utils/sh-editor-link.util';

const INITIAL_ACTIVE_FORMATS: ActiveFormatsState = {
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    bulletList: false,
    orderedList: false,
    link: false
};

@Injectable()
export class ShEditorStateService {
    public readonly canUndo = signal(false);
    public readonly canRedo = signal(false);
    public readonly canInsertLink = signal(false);
    public readonly activeFormats = signal<ActiveFormatsState>({ ...INITIAL_ACTIVE_FORMATS });
    public readonly spellcheckSuggestionMenu = signal<SpellcheckSuggestionMenuState | null>(null);
    public readonly linkMenu = signal<LinkMenuState | null>(null);

    private context?: ShEditorStateServiceContext;
    private spellcheckTimer?: ReturnType<typeof setTimeout>;
    private spellcheckRunId = 0;
    private spellcheckAbortController?: AbortController;
    private spellcheckSuggestionRequestId = 0;
    private spellcheckSuggestionAbortController?: AbortController;
    private readonly spellcheckService = inject(ShEditorSpellcheckService);

    public configure(context: ShEditorStateServiceContext): void {
        this.context = context;
    }

    public destroy(): void {
        clearTimeout(this.spellcheckTimer);
        this.spellcheckTimer = undefined;
        this.cancelSpellcheckRun();
        this.cancelSpellcheckSuggestionRequest();
        this.linkMenu.set(null);
        this.spellcheckSuggestionMenu.set(null);
        this.canUndo.set(false);
        this.canRedo.set(false);
        this.canInsertLink.set(false);
        this.activeFormats.set({ ...INITIAL_ACTIVE_FORMATS });
        this.context = undefined;
    }

    public handleEditorCreated(): void {
        this.syncToolbarState();
        this.refreshSpellcheck(true);
    }

    public handleEditorUpdated(onContentChange: () => void): void {
        this.closeSpellcheckSuggestionMenu();
        onContentChange();
        this.syncToolbarState();
        this.refreshSpellcheck();
    }

    public handleEditorSelectionUpdated(): void {
        this.closeSpellcheckSuggestionMenu();
        this.syncToolbarState();
    }

    public handleDocumentMouseDown(event: MouseEvent, hostElement: HTMLElement): void {
        const target = event.target;
        if (!(target instanceof Node)) {
            this.closeMenus();
            return;
        }

        if (target instanceof HTMLElement) {
            if (
                target.closest('[data-sh-editor-link-menu="true"]') ||
                target.closest('[data-sh-editor-link-trigger="true"]')
            ) {
                return;
            }

            if (target.closest('[data-sh-editor-spellcheck-menu="true"]')) {
                return;
            }

            if (hostElement.contains(target) && target.closest('[data-sh-editor-spellcheck="true"]')) {
                return;
            }
        }

        this.closeMenus();
    }

    public handleEscapeKey(event: Event): void {
        if (!this.linkMenu() && !this.spellcheckSuggestionMenu()) {
            return;
        }

        if (event instanceof KeyboardEvent) {
            event.preventDefault();
        }

        this.closeMenus();
    }

    public applyExternalValue(normalizedValue: string): void {
        this.closeMenus();

        if (!this.editor) {
            return;
        }

        const shouldResetEditorState =
            this.getEditorValue() !== normalizedValue || this.editor.can().undo() || this.editor.can().redo();

        if (shouldResetEditorState) {
            this.resetEditorState(normalizedValue);
        }

        this.syncToolbarState();
        this.refreshSpellcheck(true);
    }

    public executeToolbarCommand(command: ToolbarCommand): void {
        if (!this.editor || this.isDisabled()) {
            return;
        }

        const chain = this.editor.chain().focus();

        switch (command) {
            case 'undo':
                chain.undo().run();
                break;
            case 'redo':
                chain.redo().run();
                break;
            case 'bold':
                chain.toggleBold().run();
                break;
            case 'italic':
                chain.toggleItalic().run();
                break;
            case 'underline':
                chain.toggleUnderline().run();
                break;
            case 'strike':
                chain.toggleStrike().run();
                break;
            case 'bulletList':
                chain.toggleBulletList().run();
                break;
            case 'orderedList':
                chain.toggleOrderedList().run();
                break;
            case 'clearFormatting':
                chain.unsetLink().unsetAllMarks().clearNodes().run();
                break;
        }

        this.syncToolbarState();
    }

    public toggleLinkMenu(event: MouseEvent): void {
        if (!this.editor || this.isDisabled() || this.editor.state.selection.empty) {
            return;
        }

        if (this.linkMenu()) {
            this.closeLinkMenu(true);
            return;
        }

        const triggerElement =
            event.currentTarget instanceof HTMLElement
                ? event.currentTarget
                : event.target instanceof HTMLElement
                  ? event.target.closest<HTMLElement>('[data-sh-editor-link-trigger="true"]')
                  : null;
        if (!triggerElement) {
            return;
        }

        const { from, to } = this.editor.state.selection;
        const selectedText = this.editor.state.doc.textBetween(from, to, ' ').trim();
        const currentHref = normalizeEditorLinkHref(
            (this.editor.getAttributes('link')['href'] as string | undefined) ?? ''
        );
        const rect = triggerElement.getBoundingClientRect();
        const menuWidth = 340;
        const menuHeight = 324;

        this.closeSpellcheckSuggestionMenu();
        this.linkMenu.set({
            selectedText,
            href: currentHref,
            hasLink: Boolean(currentHref && currentHref !== 'https://'),
            from,
            to,
            top: Math.max(16, Math.min(rect.bottom + 10, window.innerHeight - menuHeight - 16)),
            left: Math.max(16, Math.min(rect.left - menuWidth + rect.width, window.innerWidth - menuWidth - 16))
        });
        this.markForCheck();
    }

    public closeLinkMenu(restoreSelection = false): void {
        const menu = this.linkMenu();
        this.linkMenu.set(null);

        if (restoreSelection && menu && this.editor && !this.isDisabled()) {
            this.editor.chain().focus().setTextSelection({ from: menu.from, to: menu.to }).run();
            this.syncToolbarState();
            return;
        }

        this.markForCheck();
    }

    public closeMenus(): void {
        this.closeLinkMenu();
        this.closeSpellcheckSuggestionMenu();
    }

    public async handleSpellcheckWordClick(event: MouseEvent): Promise<void> {
        this.closeLinkMenu();

        const target = event.target;
        if (!(target instanceof Node)) {
            this.closeSpellcheckSuggestionMenu();
            return;
        }

        const targetElement = (target instanceof HTMLElement ? target : target.parentElement)?.closest<HTMLElement>(
            '[data-sh-editor-spellcheck="true"]'
        );

        if (!targetElement) {
            this.closeSpellcheckSuggestionMenu();
            return;
        }

        const from = Number(targetElement.getAttribute('data-sh-editor-from'));
        const to = Number(targetElement.getAttribute('data-sh-editor-to'));
        if (!Number.isFinite(from) || !Number.isFinite(to)) {
            this.closeSpellcheckSuggestionMenu();
            return;
        }

        const rect = targetElement.getBoundingClientRect();
        const menuWidth = 220;
        const menuHeight = 88;
        const menuTop = Math.max(16, Math.min(rect.bottom + 8, window.innerHeight - menuHeight - 16));
        const menuLeft = Math.max(16, Math.min(rect.left, window.innerWidth - menuWidth - 16));
        const word = targetElement.getAttribute('data-sh-editor-word') ?? targetElement.textContent?.trim() ?? '';
        const requestId = this.startSpellcheckSuggestionRequest();
        const abortController = new AbortController();
        this.spellcheckSuggestionAbortController = abortController;

        this.spellcheckSuggestionMenu.set({
            word,
            loading: true,
            suggestions: [],
            from,
            to,
            top: menuTop,
            left: menuLeft
        });
        this.markForCheck();

        const suggestions = await this.spellcheckService.getSuggestions(word, abortController.signal);
        if (abortController.signal.aborted || requestId !== this.spellcheckSuggestionRequestId) {
            this.finalizeSpellcheckSuggestionRequest(abortController);
            return;
        }

        this.finalizeSpellcheckSuggestionRequest(abortController);
        if (!suggestions.length) {
            this.spellcheckSuggestionMenu.set(null);
            this.markForCheck();
            return;
        }

        this.spellcheckSuggestionMenu.set({
            word,
            loading: false,
            suggestions,
            from,
            to,
            top: menuTop,
            left: menuLeft
        });
        this.markForCheck();
    }

    public closeSpellcheckSuggestionMenu(): void {
        this.cancelSpellcheckSuggestionRequest();
        this.spellcheckSuggestionMenu.set(null);
        this.markForCheck();
    }

    public applyLinkFromMenu(submission: LinkMenuSubmission): void {
        const menu = this.linkMenu();
        const href = submission.href;
        const linkText = submission.text.trim();
        if (!menu || !this.editor || !href || !linkText) {
            return;
        }

        this.editor
            .chain()
            .focus()
            .insertContentAt(
                { from: menu.from, to: menu.to },
                {
                    type: 'text',
                    text: linkText,
                    marks: [
                        {
                            type: 'link',
                            attrs: {
                                href,
                                target: '_blank',
                                rel: 'noopener noreferrer'
                            }
                        }
                    ]
                }
            )
            .run();

        this.closeLinkMenu();
        this.syncToolbarState();
    }

    public removeLinkFromMenu(linkText: string): void {
        const menu = this.linkMenu();
        if (!menu || !this.editor || !linkText) {
            return;
        }

        this.editor
            .chain()
            .focus()
            .insertContentAt(
                { from: menu.from, to: menu.to },
                {
                    type: 'text',
                    text: linkText
                }
            )
            .run();

        this.closeLinkMenu();
        this.syncToolbarState();
    }

    public applySpellcheckSuggestion(suggestion: string): void {
        const menu = this.spellcheckSuggestionMenu();
        if (!menu || !this.editor) {
            return;
        }

        const replacementRange = this.resolveSpellcheckReplacementRange(menu);
        this.editor.chain().focus().insertContentAt(replacementRange, suggestion).run();
        this.closeSpellcheckSuggestionMenu();
    }

    public resetEditorState(content: string): void {
        if (!this.editor) {
            return;
        }

        clearTimeout(this.spellcheckTimer);
        this.spellcheckTimer = undefined;
        this.cancelSpellcheckRun();
        this.closeLinkMenu();
        this.closeSpellcheckSuggestionMenu();

        const document = createDocument(content, this.editor.schema, this.editor.options.parseOptions, {
            errorOnInvalidContent: this.editor.options.enableContentCheck
        });
        const state = EditorState.create({
            doc: document,
            schema: this.editor.schema,
            plugins: this.editor.extensionManager.plugins
        });

        this.editor.view.updateState(state);
    }

    public syncToolbarState(): void {
        if (!this.editor) {
            return;
        }

        this.canUndo.set(this.editor.can().undo());
        this.canRedo.set(this.editor.can().redo());
        this.canInsertLink.set(!this.editor.state.selection.empty);
        this.activeFormats.set({
            bold: this.editor.isActive('bold'),
            italic: this.editor.isActive('italic'),
            underline: this.editor.isActive('underline'),
            strike: this.editor.isActive('strike'),
            bulletList: this.editor.isActive('bulletList'),
            orderedList: this.editor.isActive('orderedList'),
            link: this.editor.isActive('link')
        });
        this.markForCheck();
    }

    private get editor(): Editor | null {
        return this.context?.getEditor() ?? null;
    }

    private getEditorValue(): string {
        return this.context?.getEditorValue() ?? '';
    }

    private getSpellcheckDelay(): number {
        return this.context?.getSpellcheckDelay() ?? 0;
    }

    private isDisabled(): boolean {
        return this.context?.isDisabled() ?? false;
    }

    private emitSpellingErrors(errors: string[]): void {
        this.context?.emitSpellingErrors(errors);
    }

    private markForCheck(): void {
        this.context?.markForCheck();
    }

    private refreshSpellcheck(immediate = false): void {
        clearTimeout(this.spellcheckTimer);
        const runId = this.cancelSpellcheckRun();

        if (immediate) {
            void this.applySpellcheck(runId);
            return;
        }

        this.spellcheckTimer = setTimeout(() => {
            void this.applySpellcheck(runId);
        }, this.getSpellcheckDelay());
    }

    private async applySpellcheck(runId: number): Promise<void> {
        if (!this.editor) {
            return;
        }

        const doc = this.editor.state.doc;
        if (this.getEditorValue() === '') {
            clearSpellcheckDecorations(this.editor);
            this.emitSpellingErrors([]);
            return;
        }

        const abortController = new AbortController();
        this.spellcheckAbortController = abortController;

        const matches = await this.spellcheckService.findMisspellings(doc, abortController.signal);
        if (
            abortController.signal.aborted ||
            runId !== this.spellcheckRunId ||
            !this.editor ||
            !this.editor.state.doc.eq(doc)
        ) {
            this.finalizeSpellcheckRun(abortController);
            return;
        }

        updateSpellcheckDecorations(this.editor, matches);
        this.emitSpellingErrors([...new Set(matches.map((match) => match.word.trim()).filter(Boolean))]);
        this.finalizeSpellcheckRun(abortController);
    }

    private cancelSpellcheckRun(): number {
        this.spellcheckAbortController?.abort();
        this.spellcheckAbortController = undefined;
        this.spellcheckRunId += 1;

        return this.spellcheckRunId;
    }

    private finalizeSpellcheckRun(abortController: AbortController): void {
        if (this.spellcheckAbortController === abortController) {
            this.spellcheckAbortController = undefined;
        }
    }

    private startSpellcheckSuggestionRequest(): number {
        this.cancelSpellcheckSuggestionRequest();
        this.spellcheckSuggestionRequestId += 1;

        return this.spellcheckSuggestionRequestId;
    }

    private cancelSpellcheckSuggestionRequest(): void {
        this.spellcheckSuggestionAbortController?.abort();
        this.spellcheckSuggestionAbortController = undefined;
    }

    private finalizeSpellcheckSuggestionRequest(abortController: AbortController): void {
        if (this.spellcheckSuggestionAbortController === abortController) {
            this.spellcheckSuggestionAbortController = undefined;
        }
    }

    private resolveSpellcheckReplacementRange(menu: SpellcheckSuggestionMenuState): { from: number; to: number } {
        const matchedRange = this.findSpellcheckWordRange(menu.word, menu.from, menu.to);

        if (matchedRange) {
            return matchedRange;
        }

        return this.expandToTokenRange(menu.from, menu.to);
    }

    private findSpellcheckWordRange(
        word: string,
        expectedFrom: number,
        expectedTo: number
    ): { from: number; to: number } | null {
        if (!this.editor || !word) {
            return null;
        }

        const documentSize = this.editor.state.doc.content.size;
        const searchPadding = Math.max(4, word.length + 2);
        const searchFrom = Math.max(1, expectedFrom - searchPadding);
        const searchTo = Math.min(documentSize, expectedTo + searchPadding);
        const searchText = this.editor.state.doc.textBetween(searchFrom, searchTo, '\n', '\n');
        let matchIndex = searchText.indexOf(word);
        let bestMatch: { from: number; to: number; distance: number } | null = null;

        while (matchIndex !== -1) {
            const from = searchFrom + matchIndex;
            const to = from + word.length;
            const charBefore = this.getDocumentCharacter(from - 1);
            const charAfter = this.getDocumentCharacter(to);

            if (this.isTokenBoundary(charBefore) && this.isTokenBoundary(charAfter)) {
                const distance = Math.abs(from - expectedFrom) + Math.abs(to - expectedTo);

                if (!bestMatch || distance < bestMatch.distance) {
                    bestMatch = { from, to, distance };
                }
            }

            matchIndex = searchText.indexOf(word, matchIndex + 1);
        }

        return bestMatch ? { from: bestMatch.from, to: bestMatch.to } : null;
    }

    private expandToTokenRange(initialFrom: number, initialTo: number): { from: number; to: number } {
        let from = initialFrom;
        let to = initialTo;

        while (from < to && this.isTokenBoundary(this.getDocumentCharacter(from))) {
            from += 1;
        }

        while (to > from && this.isTokenBoundary(this.getDocumentCharacter(to - 1))) {
            to -= 1;
        }

        while (from > 1 && !this.isTokenBoundary(this.getDocumentCharacter(from - 1))) {
            from -= 1;
        }

        while (!this.isTokenBoundary(this.getDocumentCharacter(to))) {
            to += 1;
        }

        return { from, to };
    }

    private getDocumentCharacter(position: number): string {
        if (!this.editor || position < 1 || position > this.editor.state.doc.content.size) {
            return '';
        }

        return this.editor.state.doc.textBetween(position, position + 1, '', '');
    }

    private isTokenBoundary(character: string): boolean {
        return !character || /\s/.test(character);
    }
}
